import { useMountEffect, useToggle } from "@react-hookz/web";
import { useAtomValue, useSetAtom } from "jotai";
import { useRouter } from "next/router";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";

import { SanitizedUrlKeys, useParsedCookie } from "shared/hooks";
import { accountAtom, registerEmailCookieAtom } from "shared/lib/account";
import { useRouterReplaceWithSanitizedKeys } from "shared/lib/router";

import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { OnboardingCTAType } from "scmp-app/components/tracking/google-analytics-4/types";
import { useFetchUserTypes, useGetUserType } from "scmp-app/lib/hooks";
import { rosettaAtom } from "scmp-app/lib/rosetta";

import { generateRecommendedPromises, sanitizePromisesToMap } from "./apis";
import type {
  OnboardingSlide,
  OnboardQueryStringPayload,
  OnboardUserType,
  RecommendedContentEntityIds,
} from "./types";

export const useNeedToShowOnboarding = () => {
  const parsedQueryString = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  }) as unknown as OnboardQueryStringPayload;
  return parsedQueryString.onboard === "true";
};

export const useGetOnboardingUserType = () => {
  const { fetchUserTypes, userTypes } = useFetchUserTypes();
  useMountEffect(() => {
    void fetchUserTypes();
  });
  const { getUserType } = useGetUserType();
  const userType = getUserType(userTypes);
  if (userType === "018") return "default";
  return userType;
};

interface HandleSlideTrackImpressionParameters {
  ctaType: OnboardingCTAType;
  currentSlide: number;
  userType: OnboardUserType;
}

export const useTrackImpression = () => {
  const handleSlideTrackImpression = (parameters: HandleSlideTrackImpressionParameters) => {
    const { ctaType, currentSlide, userType } = parameters;

    sendGA4Tracking({
      action: "imp",
      category: "onboarding",
      customized_parameters: {
        cta_type: ctaType,
        step: currentSlide,
        user_type: userType,
      },
      subcategory: "",
    });
  };

  return {
    handleSlideTrackImpression,
  };
};

export const useOnboarding = () => {
  const [entityIdMap, setEntityIdMap] = useState<
    null | Record<OnboardingSlide, RecommendedContentEntityIds> | Record<string, never>
  >();
  const [isShowOnboardingModal, setIsShowOnboardingModal] = useToggle(false);
  const [onboardingSlides, setOnboardingSlides] = useState<OnboardingSlide[]>([]);
  const [userType, setUserType] = useState<null | OnboardUserType>(null);

  const { openLoginDialog } = useLoginDialogStateHelper();
  const { user } = useAtomValue(accountAtom);
  const { pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();

  const { value: forcedQaUserType } = useParsedCookie("qa_user_type", raw => raw, {
    initializeWithValue: true,
  });

  const userTypeToOnboardingFlow: Record<OnboardUserType, OnboardingSlide[]> = useMemo(
    () => ({
      "008": ["newsletterSubscription", "conversations", "rosettaSubscription"],
      "014": ["myNews", "recommendedArticles", "rosettaSubscription"],
      "016": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "017": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "028": ["newsletterSubscription", "myNews", "recommendedArticles"],
      "029": ["newsletterSubscription", "myNews", "recommendedArticles"],
      "030": ["myNews", "evergreenArticles", "rosettaSubscription"],
      "038": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "057": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "058": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "083": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "084": ["myNews", "newsletterSubscription", "recommendedArticles"],
      amp: ["myNews", "newsletterSubscription", "appDownload"],
      default: ["newsletterSubscription", "myNews", "rosettaSubscription"],
      myNews: ["newsletterSubscription", "appDownload", "rosettaSubscription"],
      newsletter: ["myNews", "appDownload", "rosettaSubscription"],
      subscribers: ["myNews", "newsletterSubscription", "appDownload"],
    }),
    [],
  );

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const updateRegisterEmailCookie = useSetAtom(registerEmailCookieAtom);

  const needToShow = useNeedToShowOnboarding();
  const onboardingUserType = useGetOnboardingUserType();

  useEffect(() => {
    if (!needToShow || asyncRosettaState?.result?.isScmpSubscriber === undefined) return;

    // NOTE: Showing the modal can also be triggered at popup-container/sign-up-action-redirect,
    //       that is why state is handled by global state and not useState
    const { onboard } = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as OnboardQueryStringPayload;

    setUserType(onboardingUserType);
    setOnboardingSlides(userTypeToOnboardingFlow[onboardingUserType]);

    if (onboard && onboard !== "true") {
      void replace(
        SanitizedUrlKeys,
        { pathname, query },
        {
          shallow: true,
        },
      );

      return;
    } else if (onboard === "true" && !user?.uuid) {
      openLoginDialog({
        destination: window.location.href,
        ga4CustomParameter: {
          trigger_point: "onboarding",
        },
      });
    } else if (onboard === "true" && user?.uuid) {
      // RYI: components/home/news-ticker/account-activation/index.tsx
      updateRegisterEmailCookie(null);
      setIsShowOnboardingModal(true);
    }
  }, [
    forcedQaUserType,
    openLoginDialog,
    pathname,
    query,
    replace,
    setIsShowOnboardingModal,
    user?.lvl,
    user?.source,
    user?.uuid,
    userTypeToOnboardingFlow,
    needToShow,
    onboardingUserType,
    asyncRosettaState?.result?.isScmpSubscriber,
    updateRegisterEmailCookie,
  ]);

  useEffect(() => {
    if (!isShowOnboardingModal) return;

    const run = async () => {
      const requiredPromises = generateRecommendedPromises(onboardingSlides, user?.uuid ?? "");
      if (requiredPromises.length === 0) {
        setEntityIdMap({});
      } else {
        const resolvedPromises = await Promise.all(
          requiredPromises.map(async promise => await promise()()),
        );
        setEntityIdMap(sanitizePromisesToMap(resolvedPromises));
      }
    };
    void run();
  }, [onboardingSlides, user?.uuid, isShowOnboardingModal]);

  const shouldShowOnboarding = isShowOnboardingModal && userType !== null && entityIdMap !== null;

  return {
    entityIdMap,
    isShowOnboardingModal,
    onboardingSlides,
    setIsShowOnboardingModal,
    shouldShowOnboarding,
    userType,
  };
};
